import { QueryType } from "@front/m19-api-client";
import { AdType } from "@m19-board/shared/compact-filter/compact-filter.component";
import {
  DataTableDataset,
  DonutDataset,
  LineChartDataset,
  MetricColumn,
  VariationTableSortBy,
  ViewBy,
  WidgetType,
} from "@m19-board/widget/widget.model";

export interface WidgetConfig<T extends WidgetType> {
  type: T;
  id: string;
  title: string;
  w: number;
  h: number;
  x?: number;
  y?: number;
}

export interface ProductGroupConfig {
  productGroupId?: number | number[];
}

export interface BrandConfig {
  brandIds?: number[];
}

export interface ProductTagFilterConfig {
  customField1?: string[];
  customField2?: string[];
}

export interface AdTypeConfig {
  adTypes?: AdType[];
}

export interface ColorConfig {
  color?: string;
}

export interface ViewByConfig {
  viewBy: ViewBy;
}

export interface AsinLimitConfig {
  asinLimit?: number;
}

export interface DonutDataSetConfig {
  dataSet?: DonutDataset;
}

export function areProductTagsSet(productTagFilters: ProductTagFilterConfig) {
  return (
    (productTagFilters.customField1 && productTagFilters.customField1.length > 0) ||
    (productTagFilters.customField2 && productTagFilters.customField2.length > 0)
  );
}

export function hasProductTag1(config: any): config is { customField1: string[] } {
  const productTag1 = (config as ProductTagFilterConfig).customField1;
  return !!productTag1 && !!productTag1.length;
}

export function hasProductTag2(config: any): config is { customField2: string[] } {
  const productTag2 = (config as ProductTagFilterConfig).customField2;
  return !!productTag2 && !!productTag2.length;
}

export function hasProductTags(config: any): config is ProductTagFilterConfig {
  return hasProductTag1(config) || hasProductTag2(config);
}

export function hasAdTypes(config: any): config is AdTypeConfig {
  return config && config.adTypes !== undefined && config.adTypes.length > 0;
}

export function hasAsinFilters(config: WidgetConfig<any>) {
  return hasProductTags(config) || isProductGroupConfig(config) || isBrandConfig(config);
}

export function isProductGroupConfig(config: any): config is ProductGroupConfig {
  if (config.productGroupId === undefined) {
    return false;
  }
  const productGroupId = config.productGroupId;
  if (typeof productGroupId === "number") {
    return productGroupId > 0;
  }
  return productGroupId.length > 0;
}

export function isBrandConfig(config: any): config is BrandConfig {
  return (config as BrandConfig).brandIds !== undefined && (config as BrandConfig).brandIds!.length > 0;
}

export function isColorConfig(config: any): config is ColorConfig {
  return (config as ColorConfig).color !== undefined;
}

export function isViewByConfig(config: any): config is ViewByConfig {
  return (config as ViewByConfig).viewBy !== undefined;
}

export function isAsinLimitConfig(config: any): config is AsinLimitConfig {
  return (config as AsinLimitConfig).asinLimit !== undefined;
}

export interface DataTableWidgetConfig
  extends WidgetConfig<WidgetType.DataTable>,
    ProductTagFilterConfig,
    ProductGroupConfig,
    BrandConfig,
    AsinLimitConfig,
    AdTypeConfig {
  type: WidgetType.DataTable;
  metricColumns?: MetricColumn[];
  dataset: DataTableDataset;
  sortByMetricId?: string;
  sortOrder?: "asc" | "desc";
  customField?: "field1" | "field2";
  queryType?: QueryType;
}

export interface ScoreCardWidgetConfig
  extends WidgetConfig<WidgetType.ScoreCard>,
    ProductTagFilterConfig,
    ProductGroupConfig,
    BrandConfig,
    ColorConfig,
    AdTypeConfig {
  type: WidgetType.ScoreCard;
  metricId: string;
}

export interface LineChartWidgetConfig
  extends WidgetConfig<WidgetType.LineChart>,
    ProductGroupConfig,
    BrandConfig,
    ProductTagFilterConfig,
    AdTypeConfig {
  type: WidgetType.LineChart;
  metric1: string;
  metric2?: string;
  metric3?: string;
  lineChartDataset?: LineChartDataset;
}

export interface KPIWidgetConfig
  extends WidgetConfig<WidgetType.WeeklyStats>,
    BrandConfig,
    ProductTagFilterConfig,
    ProductGroupConfig,
    ViewByConfig,
    AdTypeConfig {
  type: WidgetType.WeeklyStats;
  metrics: string[];
}

export interface VariationTableWidgetConfig
  extends WidgetConfig<WidgetType.VariationTable>,
    ProductTagFilterConfig,
    ProductGroupConfig,
    BrandConfig,
    ViewByConfig,
    AdTypeConfig {
  type: WidgetType.VariationTable;
  metric: string;
  sortBy?: VariationTableSortBy;
  sortOrder?: "asc" | "desc";
}

export interface ProfitGraphWidgetConfig
  extends WidgetConfig<WidgetType.ProfitGraph>,
    ProductGroupConfig,
    ProductTagFilterConfig,
    BrandConfig {
  type: WidgetType.ProfitGraph;
  metrics: string[];
}

export interface DonutChartWidgetConfig
  extends WidgetConfig<WidgetType.DonutChart>,
    ProductGroupConfig,
    BrandConfig,
    ProductTagFilterConfig,
    DonutDataSetConfig {
  type: WidgetType.DonutChart;
  metricId: string;
  customField?: "field1" | "field2"; // custom field  donut  chart
}

export interface BarChartWidgetConfig
  extends WidgetConfig<WidgetType.BarChart>,
    ProductGroupConfig,
    BrandConfig,
    ProductTagFilterConfig,
    DonutDataSetConfig {
  type: WidgetType.BarChart;
  metricId: string;
  customField?: "field1" | "field2"; // custom field  bar  chart
}

export interface ProductViewWidgetConfig extends WidgetConfig<WidgetType.ProductView> {
  type: WidgetType.ProductView;
}

export interface ParentAsinStatsWidgetConfig extends WidgetConfig<WidgetType.ParentAsinStats> {
  type: WidgetType.ParentAsinStats;
  metrics: string[];
}
